import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import { HxAlert, HxButton, HxCard, HxInput } from "../components/hx";
// The template's Field and FieldLabel, as its login form draws them: HxField
// has no htmlFor, and the label must name the code field.
import { Field, FieldLabel } from "../components/ui/field";
import { ApiError, opsFetch } from "../api";
import type { Me } from "../App";

interface Props {
  me: Me;
  onEnrolled: () => Promise<void> | void;
}

interface EnrollInitResponse {
  qrDataUrl: string;
  otpauthUrl: string;
  manualKey: string;
}

interface EnrollConfirmResponse {
  recoveryCodes: string[];
}

type Phase =
  | { kind: "loading" }
  | { kind: "showQr"; data: EnrollInitResponse }
  | { kind: "confirming"; data: EnrollInitResponse }
  | { kind: "showCodes"; codes: string[] }
  | { kind: "error"; message: string };

export default function TotpEnroll({ me, onEnrolled }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [code, setCode] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    opsFetch<EnrollInitResponse>("/ops/totp/enroll")
      .then((data) => {
        if (!cancelled) setPhase({ kind: "showQr", data });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === "not_implemented") {
          setPhase({
            kind: "error",
            message: "TOTP enroll not wired up yet (Phase 0 step 5 pending).",
          });
        } else {
          setPhase({
            kind: "error",
            message: "Couldn't start TOTP enrollment. Try reloading.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function confirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (phase.kind !== "showQr") return;
    setSubmitError(null);
    setPhase({ kind: "confirming", data: phase.data });

    try {
      const res = await opsFetch<EnrollConfirmResponse>("/ops/totp/enroll/confirm", {
        method: "POST",
        json: { code: code.trim() },
      });
      setPhase({ kind: "showCodes", codes: res.recoveryCodes });
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_code") {
        setSubmitError("That code didn't match. Try the current one.");
      } else {
        setSubmitError("Couldn't confirm. Try again.");
      }
      setPhase({ kind: "showQr", data: phase.data });
    }
  }

  async function finish() {
    await onEnrolled();
    navigate("/", { replace: true });
  }

  if (phase.kind === "loading") {
    return (
      <AuthShell title="Preparing TOTP enrollment…">
        <p className="text-sm text-muted-foreground">Generating your secret…</p>
      </AuthShell>
    );
  }

  if (phase.kind === "error") {
    return (
      <AuthShell title="Enrollment error">
        <HxAlert className="mb-0">{phase.message}</HxAlert>
      </AuthShell>
    );
  }

  // The template has no screen for this content (helix-ui-migration's map):
  // the QR and the recovery codes each sit in a card, and the code field is
  // the template's Field. Record: docs/testing/ui-migration/089-totp-enroll.md.
  if (phase.kind === "showCodes") {
    return (
      <AuthShell
        title="Save your recovery codes"
        subtitle="Store these somewhere safe (1Password, a printed sheet in a locked drawer). Each works exactly once if you lose access to your authenticator app. They won't be shown again."
      >
        <div className="flex flex-col gap-4">
          <HxCard className="grid grid-cols-2 gap-2 font-mono text-sm">
            {phase.codes.map((c) => (
              <span key={c} className="rounded-md bg-muted px-2 py-1 text-center">
                {c}
              </span>
            ))}
          </HxCard>
          <p className="text-xs text-muted-foreground">TOTP is now enabled for {me.email}.</p>
          <HxButton variant="primary" onClick={finish} className="w-full">
            I've saved them, continue
          </HxButton>
        </div>
      </AuthShell>
    );
  }

  const data = phase.data;
  const busy = phase.kind === "confirming";

  return (
    <AuthShell
      title="Set up your authenticator"
      subtitle="Open Google Authenticator, 1Password, Authy, or any RFC 6238-compatible app and scan the QR code."
    >
      <form onSubmit={confirm} className="flex flex-col gap-4">
        <HxCard className="flex flex-col items-center gap-3">
          <img src={data.qrDataUrl} alt="TOTP QR code" className="block max-w-full rounded-md" />
          <p className="text-center text-xs text-muted-foreground">
            Can't scan? Enter this key manually:{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono break-all">{data.manualKey}</code>
          </p>
        </HxCard>
        <Field className="gap-1.5">
          <FieldLabel htmlFor="code">Confirm with the current 6-digit code</FieldLabel>
          <HxInput
            id="code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            autoFocus
            className="w-full font-mono tracking-wider"
          />
        </Field>
        <HxButton type="submit" variant="primary" disabled={busy || code.length !== 6} className="w-full">
          {busy ? "Confirming…" : "Confirm and enable"}
        </HxButton>
        {submitError && (
          <HxAlert aria-live="polite" className="mb-0">
            {submitError}
          </HxAlert>
        )}
      </form>
    </AuthShell>
  );
}
